import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  MapPin, 
  Users, 
  Globe, 
  ArrowRight,
  CheckCircle,
  Star,
  Building2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import EnhancedCTA from '@/components/conversion/EnhancedCTA';
import SwissTestimonials from '@/components/trust/SwissTestimonials';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const EuropeanCityServicePage: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract city from URL path
  const pathParts = location.pathname.split('/').filter(Boolean);
  const citySlug = pathParts[pathParts.length - 1];
  
  // Map URL slug to city display names
  const cityDisplayMap: Record<string, { name: string; country: string }> = {
    'stuttgart': { name: 'Stuttgart', country: 'Germany' },
    'muenchen': { name: 'Munich', country: 'Germany' },
    'munich': { name: 'Munich', country: 'Germany' },
    'monaco': { name: 'Munich', country: 'Germany' },
    'lyon': { name: 'Lyon', country: 'France' },
    'lione': { name: 'Lyon', country: 'France' },
    'paris': { name: 'Paris', country: 'France' },
    'parigi': { name: 'Paris', country: 'France' },
    'mailand': { name: 'Milan', country: 'Italy' },
    'milan': { name: 'Milan', country: 'Italy' },
    'milano': { name: 'Milan', country: 'Italy' },
    'como': { name: 'Como', country: 'Italy' },
    'come': { name: 'Como', country: 'Italy' }
  };
  
  const cityInfo = cityDisplayMap[citySlug] || { name: 'Stuttgart', country: 'Germany' };
  const service = 'AI-Videos';

  const translations = {
    de: {
      heroTitle: `${service} Agentur ${cityInfo.name}`,
      heroSubtitle: `Schweizer Präzision trifft auf ${cityInfo.country} Innovation`,
      heroDescription: `Als führende AI-gestützte Digitalagentur mit Schweizer Wurzeln bringen wir lokale ${cityInfo.name} Expertise und internationale Standards zusammen.`,
      getQuote: 'Kostenlos anfragen',
      calculator: 'Kosten berechnen'
    },
    en: {
      heroTitle: `${service} Agency ${cityInfo.name}`,
      heroSubtitle: `Swiss Precision Meets ${cityInfo.country} Innovation`,
      heroDescription: `As the leading AI-powered digital agency with Swiss roots, we combine local ${cityInfo.name} expertise with international standards.`,
      getQuote: 'Get Free Quote',
      calculator: 'Calculate Costs'
    },
    fr: {
      heroTitle: `Agence ${service} ${cityInfo.name}`,
      heroSubtitle: `La Précision Suisse Rencontre l'Innovation ${cityInfo.country}`,
      heroDescription: `En tant qu'agence digitale leader alimentée par l'IA avec des racines suisses, nous combinons l'expertise locale de ${cityInfo.name} avec des standards internationaux.`,
      getQuote: 'Demande Gratuite',
      calculator: 'Calculer les Coûts'
    },
    it: {
      heroTitle: `Agenzia ${service} ${cityInfo.name}`,
      heroSubtitle: `La Precisione Svizzera Incontra l'Innovazione ${cityInfo.country}`,
      heroDescription: `Come agenzia digitale leader alimentata dall'AI con radici svizzere, combiniamo l'expertise locale di ${cityInfo.name} con standard internazionali.`,
      getQuote: 'Richiesta Gratuita',
      calculator: 'Calcola i Costi'
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const handleContactClick = () => {
    navigate(`/${currentLanguage}/${currentLanguage === 'de' ? 'kontakt' : currentLanguage === 'it' ? 'contatto' : 'contact'}`);
  };

  const handleCalculatorClick = () => {
    navigate(`/${currentLanguage}/calculator`);
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-primary-glow/5" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <Badge variant="secondary" className="mb-6 bg-primary/20 text-primary border-primary/30">
                <Globe className="w-4 h-4 mr-2" />
                Swiss Quality • {cityInfo.country} Expertise
              </Badge>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                {t.heroTitle}
              </h1>

              <p className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                {t.heroSubtitle}
              </p>

              <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
                {t.heroDescription}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleContactClick}
                  className="neo-button px-8 py-3"
                >
                  {t.getQuote}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button 
                  onClick={handleCalculatorClick}
                  variant="outline"
                  className="px-8 py-3"
                >
                  {t.calculator}
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials */}
        <SwissTestimonials />

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 to-primary-glow/5">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <EnhancedCTA variant="inline" service="ai-video" />
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default EuropeanCityServicePage;